import { adminApi, ensureArray } from '../services/api';
import { useState, useEffect } from 'react';

interface CurrencyRate {
  currencyCode: string;
  rateToNgn: number;
  lastUpdated: string;
}

function isRateStale(lastUpdated: string): boolean {
  const ageMs = Date.now() - new Date(lastUpdated).getTime();
  return ageMs > 7 * 24 * 60 * 60 * 1000;
}

interface CountryConfig {
  code: string;
  name: string;
  currency: string;
  regulatoryStatus: string;
  smsProvider: string;
  paymentProvider: string;
}

export function ExpansionPage() {
  const [rates, setRates] = useState<CurrencyRate[]>([]);
  const [countries, setCountries] = useState<CountryConfig[]>([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [newRate, setNewRate] = useState('');
  const [msg, setMsg] = useState('');

  const loadRates = () =>
    adminApi<{ data?: CurrencyRate[] }>('/expansion/rates').then((d) =>
      setRates(ensureArray(d.data))
    );

  const loadCountries = () =>
    adminApi<{ data?: CountryConfig[] }>('/expansion/countries').then((d) =>
      setCountries(ensureArray(d.data))
    );

  useEffect(() => {
    Promise.all([loadRates(), loadCountries()]).catch(() => {});
  }, []);

  const updateRate = async (currency: string) => {
    if (!newRate) return;
    await adminApi(`/expansion/rates/${currency}`, {
      method: 'PUT',
      body: JSON.stringify({ rateToNgn: parseFloat(newRate) }),
    });
    setMsg(`Rate updated for ${currency}`);
    setTimeout(() => setMsg(''), 3000);
    setNewRate('');
    await loadRates();
  };

  const updateCountry = async (code: string, field: string, value: string) => {
    await adminApi(`/expansion/countries/${code}`, {
      method: 'PUT',
      body: JSON.stringify({ [field]: value }),
    });
    setMsg(`Country ${code} updated`);
    setTimeout(() => setMsg(''), 3000);
    await loadCountries();
  };

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold text-gray-900">Expansion & Currencies</h1>
      {msg && <div className="mb-4 rounded-lg bg-green-100 p-3 text-sm text-green-800">{msg}</div>}

      {rates.some((r) => r.currencyCode !== 'NGN' && isRateStale(r.lastUpdated)) && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <strong>Stale exchange rates detected.</strong> One or more non-NGN rates have not been
          updated in over 7 days. Multi-currency bookings include a 2% buffer, but you should
          refresh rates before processing GHS/XOF payments.
        </div>
      )}

      <div className="card mb-6">
        <h2 className="mb-3 text-lg font-semibold">Currency Rates (to NGN)</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 pr-4">Currency</th>
                <th className="py-2 pr-4">Rate to 1 NGN</th>
                <th className="py-2 pr-4">Last Updated</th>
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => (
                <tr
                  key={r.currencyCode}
                  className={`border-b ${r.currencyCode !== 'NGN' && isRateStale(r.lastUpdated) ? 'bg-amber-50' : ''}`}
                >
                  <td className="py-2 pr-4 font-medium">
                    {r.currencyCode}
                    {r.currencyCode !== 'NGN' && isRateStale(r.lastUpdated) && (
                      <span className="ml-2 text-xs font-normal text-amber-700">(stale)</span>
                    )}
                  </td>
                  <td className="py-2 pr-4">{r.rateToNgn}</td>
                  <td className="py-2 pr-4 text-gray-500">
                    {new Date(r.lastUpdated).toLocaleDateString()}
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex gap-2">
                      <input
                        className="w-24 rounded border p-1 text-xs"
                        type="number"
                        step="0.01"
                        placeholder="New rate"
                        value={selectedCode === r.currencyCode ? newRate : ''}
                        onChange={(e) => {
                          setSelectedCode(r.currencyCode);
                          setNewRate(e.target.value);
                        }}
                      />
                      <button
                        onClick={() => updateRate(r.currencyCode)}
                        className="bg-primary-600 rounded px-2 py-1 text-xs text-white"
                      >
                        Update
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {rates.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-center text-gray-400">
                    No rates configured
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2 className="mb-3 text-lg font-semibold">Country Configurations</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 pr-4">Code</th>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Currency</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">SMS</th>
                <th className="py-2 pr-4">Payment</th>
              </tr>
            </thead>
            <tbody>
              {countries.map((c) => (
                <tr key={c.code} className="border-b">
                  <td className="py-2 pr-4 font-medium">{c.code}</td>
                  <td className="py-2 pr-4">{c.name}</td>
                  <td className="py-2 pr-4">{c.currency}</td>
                  <td className="py-2 pr-4">
                    <select
                      className="rounded border p-1 text-xs"
                      value={c.regulatoryStatus}
                      onChange={(e) => updateCountry(c.code, 'regulatoryStatus', e.target.value)}
                    >
                      <option value="active">Active</option>
                      <option value="pending_review">Pending Review</option>
                      <option value="restricted">Restricted</option>
                    </select>
                  </td>
                  <td className="py-2 pr-4">
                    <select
                      className="rounded border p-1 text-xs"
                      value={c.smsProvider}
                      onChange={(e) => updateCountry(c.code, 'smsProvider', e.target.value)}
                    >
                      <option value="termii">Termii</option>
                      <option value="twilio">Twilio</option>
                    </select>
                  </td>
                  <td className="py-2 pr-4">
                    <select
                      className="rounded border p-1 text-xs"
                      value={c.paymentProvider}
                      onChange={(e) => updateCountry(c.code, 'paymentProvider', e.target.value)}
                    >
                      <option value="paystack">Paystack</option>
                      <option value="cinetpay">CinetPay</option>
                      <option value="flutterwave">Flutterwave</option>
                    </select>
                  </td>
                </tr>
              ))}
              {countries.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-gray-400">
                    No countries configured
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
